import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import useHasAnyOfScopes from "../common/hooks/useHasAnyOfScopes";
import { CHECKOUT_API_HOSTNAME } from "../env";
import fulfill from "../fulfill";
import type { CheckoutConfig } from "../types/checkout";
import type { CheckoutConfigWrite } from "../types/checkout/generated";

const fetchCheckoutConfig = async (payload: {
    accountId: string;
}): Promise<CheckoutConfig> => {
    return await fulfill.get({
        accountId: payload.accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/admin/checkout`,
        handlers: {
            200: (data) => data,
            404: () => null,
        },
    });
};

const fetchPutCheckoutConfig = async (
    _key: string,
    options: {
        arg: {
            accountId: string;
            updatedCheckoutConfiguration: CheckoutConfigWrite;
        };
    },
): Promise<CheckoutConfig> => {
    return await fulfill.put<CheckoutConfigWrite>({
        accountId: options.arg.accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/admin/checkout`,
        json: options.arg.updatedCheckoutConfiguration,
        handlers: {
            200: (data) => data,
            404: () => null,
        },
    });
};

export const useCheckoutConfig = (accountId: string) => {
    const canReadCheckout = useHasAnyOfScopes([
        "admin:checkout",
        "read:checkout",
    ]);
    const canWriteCheckout = useHasAnyOfScopes([
        "admin:checkout",
        "write:checkout",
    ]);
    const { data, isLoading, error } = useSWR(
        () =>
            accountId && canReadCheckout
                ? {
                      key: `${accountId}/checkout`,
                      accountId,
                  }
                : null,
        fetchCheckoutConfig,
    );
    const {
        trigger: updateCheckoutConfig,
        isMutating: isUpdatingCheckoutConfig,
        error: updateCheckoutConfigError,
    } = useSWRMutation(
        () => (canWriteCheckout ? `${accountId}/checkout` : null),
        fetchPutCheckoutConfig,
    );

    return {
        checkoutConfig: data,
        isLoading,
        error,
        updateCheckoutConfig,
        isUpdatingCheckoutConfig,
        updateCheckoutConfigError,
    };
};
